import React, { useState } from "react";
import { redeemSigninLink, requestSigninLink, startGoogleLogin } from "./api";
import logoUrl from "./assets/logo.png";

declare const process: { env: { ADMIN_SIGNIN_DOMAIN?: string } };

/** The company domain admins sign in from. Build-time; the BFF enforces it. */
export const SIGNIN_DOMAIN = (process.env.ADMIN_SIGNIN_DOMAIN ?? "ivorycomcrm.com").toLowerCase();

/**
 * Pre-auth landing page with two doors that end in the same place:
 *  - "Email me a sign-in link": a one-time link to an @SIGNIN_DOMAIN address,
 *    whoever hosts the domain's mail (provider-independent, 2026-09-30);
 *  - "Continue with Google": the original Workspace SSO, still available to
 *    anyone who has a Google account on the domain.
 * Both land back here with an admin_session cookie set (or on the MFA
 * challenge). AuthGate in App.tsx then sees the cookie via /auth/me.
 */
export function Login({ error }: { error?: string }) {
  const [email, setEmail] = useState("");
  const [state, setState] = useState<"idle" | "sending" | "sent" | "failed">("idle");
  const [failure, setFailure] = useState<string | null>(null);
  const trimmed = email.trim().toLowerCase();
  const onDomain = trimmed.endsWith(`@${SIGNIN_DOMAIN}`) && trimmed.indexOf("@") > 0;

  const sendLink = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!onDomain || state === "sending") return;
    setState("sending");
    setFailure(null);
    try {
      await requestSigninLink(trimmed);
      setState("sent");
    } catch (err: any) {
      setFailure(err?.message ?? "Could not send the link. Try again in a minute.");
      setState("failed");
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-zinc-950 px-4">
      <div className="w-full max-w-md rounded-3xl bg-zinc-900 border border-zinc-800 p-8 shadow-2xl">
        <div className="flex items-center gap-3 mb-8">
          <img src={logoUrl} alt="Ivorycom" className="h-12 w-12 rounded-2xl object-cover" />
          <div>
            <h1 className="text-xl font-bold text-white">Ivorycom Admin</h1>
            <p className="text-xs text-zinc-400">Business Command Center</p>
          </div>
        </div>

        <h2 className="text-2xl font-bold text-white mb-2">Sign in</h2>
        <p className="text-sm text-zinc-400 mb-6">
          Use your @{SIGNIN_DOMAIN} address. Access is restricted to invited team members.
        </p>

        {error ? (
          <div className="mb-4 rounded-xl bg-red-500/10 border border-red-500/30 px-4 py-3 text-sm text-red-300">
            {error}
          </div>
        ) : null}

        {state === "sent" ? (
          <div className="mb-6 rounded-xl bg-emerald-500/10 border border-emerald-500/30 px-4 py-3 text-sm text-emerald-200" role="status">
            If <span className="font-medium">{trimmed}</span> is an admin, a sign-in link is on its way.
            It works once and expires in 10 minutes. Open it on this device or any other.
          </div>
        ) : (
          <form onSubmit={sendLink} className="mb-6" noValidate>
            <label htmlFor="signin-email" className="block text-xs font-medium text-zinc-400 mb-2">
              Work email
            </label>
            <input
              id="signin-email"
              type="email"
              autoComplete="email"
              inputMode="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder={`you@${SIGNIN_DOMAIN}`}
              className="w-full rounded-xl bg-zinc-950 border border-zinc-700 px-4 py-3 text-white placeholder:text-zinc-600 outline-none focus:border-zinc-400"
            />
            {trimmed && !onDomain ? (
              <p className="mt-2 text-xs text-amber-300">Only @{SIGNIN_DOMAIN} addresses can sign in.</p>
            ) : null}
            {failure ? (
              <p className="mt-2 text-xs text-red-300" role="alert">{failure}</p>
            ) : null}
            <button
              type="submit"
              disabled={!onDomain || state === "sending"}
              className="mt-3 w-full rounded-xl bg-white text-zinc-950 px-4 py-3 font-medium hover:bg-zinc-100 active:bg-zinc-200 transition disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {state === "sending" ? "Sending…" : "Email me a sign-in link"}
            </button>
          </form>
        )}

        <div className="flex items-center gap-3 mb-6" aria-hidden="true">
          <div className="h-px flex-1 bg-zinc-800" />
          <span className="text-[11px] uppercase tracking-wider text-zinc-500">or</span>
          <div className="h-px flex-1 bg-zinc-800" />
        </div>

        <button
          onClick={startGoogleLogin}
          type="button"
          className="w-full flex items-center justify-center gap-3 rounded-xl bg-zinc-800 text-white border border-zinc-700 px-4 py-3 font-medium hover:bg-zinc-700 active:bg-zinc-600 transition"
        >
          <svg width="20" height="20" viewBox="0 0 48 48" aria-hidden="true">
            <path
              d="M43.6 20.4H24v7.5h11.3C34.5 31.7 29.7 35 24 35c-6.6 0-12-5.4-12-12s5.4-12 12-12c3 0 5.7 1.1 7.8 2.9l5.6-5.6C33.6 4.9 29 3 24 3 12.4 3 3 12.4 3 24s9.4 21 21 21c10.5 0 19.8-7.5 19.8-21 0-1.4-.1-2.4-.2-3.6z"
              fill="#FFC107"
            />
            <path
              d="M6.4 14.6l6.4 4.7C14.7 16 19 13 24 13c3 0 5.7 1.1 7.8 2.9l5.6-5.6C33.6 6.9 29 5 24 5 16.5 5 9.9 9.4 6.4 14.6z"
              fill="#FF3D00"
            />
            <path
              d="M24 45c5.5 0 10.5-2.1 14.3-5.5l-6.6-5.5c-1.9 1.4-4.4 2.5-7.7 2.5-5.7 0-10.5-3.7-12.2-8.8l-6.5 5C8.4 39.6 15.6 45 24 45z"
              fill="#4CAF50"
            />
            <path
              d="M43.6 20.4H24v7.5h11.3c-.6 1.7-1.7 3.3-3.3 4.5l6.6 5.5c-.5.5 7.6-5.5 7.6-13.9 0-1.4-.1-2.4-.6-3.6z"
              fill="#1976D2"
            />
          </svg>
          Continue with Google
        </button>

        <p className="text-xs text-zinc-500 mt-6 text-center">
          Need access? Ask an OWNER to invite you.
        </p>
      </div>
    </div>
  );
}

export function LoginLoading() {
  return (
    <div className="min-h-screen flex items-center justify-center bg-zinc-950">
      <div className="text-zinc-400 text-sm flex items-center gap-3">
        <svg className="animate-spin h-5 w-5" viewBox="0 0 24 24" fill="none">
          <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="3" opacity="0.25" />
          <path d="M22 12a10 10 0 0 0-10-10" stroke="currentColor" strokeWidth="3" />
        </svg>
        Loading…
      </div>
    </div>
  );
}

/**
 * Opened from the emailed link (#signin-link=<token>). One deliberate click
 * redeems the token by POST; the GET that opened this page signs nobody in,
 * so a mail provider's link prefetch cannot spend the link. On success the
 * BFF has set admin_session (→ reload into the portal) or admin_mfa_pending
 * (→ reload into the existing #mfa-challenge screen).
 */
export function SigninLinkRedeem({ token }: { token: string }) {
  const [state, setState] = useState<"ready" | "redeeming" | "failed">("ready");
  const [failure, setFailure] = useState<string | null>(null);

  const redeem = async () => {
    if (state === "redeeming") return;
    setState("redeeming");
    setFailure(null);
    try {
      const { next } = await redeemSigninLink(token);
      window.location.hash = next === "mfa" ? "mfa-challenge" : "";
      window.location.reload();
    } catch (err: any) {
      setFailure(err?.message ?? "This link is not valid, has expired, or was already used.");
      setState("failed");
    }
  };

  const backToLogin = () => {
    window.location.hash = "";
    window.location.reload();
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-zinc-950 px-4">
      <div className="w-full max-w-md rounded-3xl bg-zinc-900 border border-zinc-800 p-8 shadow-2xl">
        <div className="flex items-center gap-3 mb-8">
          <img src={logoUrl} alt="Ivorycom" className="h-12 w-12 rounded-2xl object-cover" />
          <div>
            <h1 className="text-xl font-bold text-white">Ivorycom Admin</h1>
            <p className="text-xs text-zinc-400">Business Command Center</p>
          </div>
        </div>
        <h2 className="text-2xl font-bold text-white mb-2">Finish signing in</h2>
        <p className="text-sm text-zinc-400 mb-6">
          You opened a one-time sign-in link. Confirm to continue; the link is spent as soon as you do.
        </p>
        {failure ? (
          <div className="mb-4 rounded-xl bg-red-500/10 border border-red-500/30 px-4 py-3 text-sm text-red-300" role="alert">
            {failure}
          </div>
        ) : null}
        {state === "failed" ? (
          <button
            type="button"
            onClick={backToLogin}
            className="w-full rounded-xl bg-white text-zinc-950 px-4 py-3 font-medium hover:bg-zinc-100 active:bg-zinc-200 transition"
          >
            Request a new link
          </button>
        ) : (
          <button
            type="button"
            onClick={redeem}
            disabled={state === "redeeming"}
            autoFocus
            className="w-full rounded-xl bg-white text-zinc-950 px-4 py-3 font-medium hover:bg-zinc-100 active:bg-zinc-200 transition disabled:opacity-50"
          >
            {state === "redeeming" ? "Signing in…" : "Sign in"}
          </button>
        )}
      </div>
    </div>
  );
}

/** The token carried by the emailed link, if this page was opened from one. */
export function signinLinkTokenFromHash(hash: string): string | null {
  const m = /^#signin-link=([A-Za-z0-9_\-%]+)$/.exec(hash);
  if (!m) return null;
  try {
    return decodeURIComponent(m[1]);
  } catch {
    return null;
  }
}
